import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  RefreshControl,
  ActivityIndicator,
} from 'react-native';
import { QuickReplySetCard, QuickReplySetCardProps } from './QuickReplySetCard';
import { colors, spacing, typography } from '../../theme';
import { useThemedStyles } from '../../hooks/useThemedStyles';

export interface QuickReplySetListProps {
  quickReplySets: QuickReplySetCardProps['quickReplySet'][];
  loading?: boolean;
  refreshing?: boolean;
  onRefresh?: () => void;
  onLoadMore?: () => void;
  hasMore?: boolean;
  onQuickReplySetPress?: (setId: string) => void;
  onQuickReplySetEdit?: (setId: string) => void;
  onQuickReplySetDelete?: (setId: string) => void;
  onQuickReplySetManageItems?: (setId: string) => void;
  onQuickReplySetSend?: (setId: string) => void;
  showActions?: boolean;
  emptyMessage?: string;
  containerStyle?: any;
}

export const QuickReplySetList: React.FC<QuickReplySetListProps> = ({
  quickReplySets,
  loading = false,
  refreshing = false,
  onRefresh,
  onLoadMore,
  hasMore = false,
  onQuickReplySetPress,
  onQuickReplySetEdit,
  onQuickReplySetDelete,
  onQuickReplySetManageItems,
  onQuickReplySetSend,
  showActions = true,
  emptyMessage = 'No quick reply sets yet. Create your first set to get started!',
  containerStyle,
}) => {
  const styles = useThemedStyles(getStyles);
  const renderQuickReplySet = ({ item }: { item: QuickReplySetCardProps['quickReplySet'] }) => (
    <QuickReplySetCard
      quickReplySet={item}
      onPress={onQuickReplySetPress}
      onEdit={onQuickReplySetEdit}
      onDelete={onQuickReplySetDelete}
      onManageItems={onQuickReplySetManageItems}
      onSend={onQuickReplySetSend}
      showActions={showActions}
    />
  );

  const renderEmpty = () => {
    if (loading) {
      return (
        <View style={styles.emptyContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={styles.emptyText}>Loading quick reply sets...</Text>
        </View>
      );
    }

    return (
      <View style={styles.emptyContainer}>
        <Text style={styles.emptyText}>{emptyMessage}</Text>
      </View>
    );
  };

  const renderFooter = () => {
    if (!hasMore) return null;
    return (
      <View style={styles.footer}>
        <ActivityIndicator size="small" color={colors.primary} />
      </View>
    );
  };

  return (
    <View style={[styles.container, containerStyle]}>
      <FlatList
        data={quickReplySets}
        renderItem={renderQuickReplySet}
        keyExtractor={(item, index) => item._id || `quick-reply-set-${index}`}
        ListEmptyComponent={renderEmpty}
        ListFooterComponent={renderFooter}
        refreshControl={
          onRefresh ? (
            <RefreshControl
              refreshing={refreshing}
              onRefresh={onRefresh}
              colors={[colors.primary]}
              tintColor={colors.primary}
            />
          ) : undefined
        }
        onEndReached={hasMore && onLoadMore ? onLoadMore : undefined}
        onEndReachedThreshold={0.5}
        contentContainerStyle={[
          quickReplySets.length === 0 ? styles.emptyListContent : styles.listContent,
        ]}
        showsVerticalScrollIndicator={false}
      />
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    flex: 1,
  },
  listContent: {
    padding: spacing.md,
    paddingBottom: spacing.xl,
  },
  emptyListContent: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xl,
  },
  emptyContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing.xl * 2,
  },
  emptyText: {
    fontSize: typography.fontSize.base,
    color: colors.textLight,
    textAlign: 'center',
    fontFamily: typography.fontFamily.primary,
    marginTop: spacing.md,
  },
  footer: {
    paddingVertical: spacing.md,
    alignItems: 'center',
  },
});


